import type { Position } from 'geojson';
import type { FC } from 'react';
import { useCallback, useEffect, useState } from 'react';
import type { MarkerDragEvent } from 'react-map-gl/mapbox';
import { Marker, useMap } from 'react-map-gl/mapbox';
import { Pin } from '../markers/pin.js';

interface Props {
  onPointChange: (point: Position) => void;
  point?: Position | null;
}

export const DraggableMarkerLayer: FC<Props> = ({ point, onPointChange }) => {
  const [coordinates, setCoordinates] = useState(point);
  const [dragging, setDragging] = useState(false);
  const { current } = useMap();

  useEffect(() => {
    setCoordinates(point);
  }, [point]);

  useEffect(() => {
    if (!current) return;
    current.getCanvas().style.cursor = dragging ? 'move' : '';
  }, [current, dragging]);

  const onMarkerDragStart = useCallback(() => {
    setDragging(true);
  }, []);

  const onMarkerDrag = useCallback((event: MarkerDragEvent) => {
    setCoordinates([event.lngLat.lng, event.lngLat.lat]);
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: works, don't want to mess with it.
  const onMarkerDragEnd = useCallback(() => {
    setDragging(false);
    coordinates && onPointChange(coordinates);
  }, [coordinates]);

  if (!coordinates) return null;

  return (
    <Marker
      anchor="bottom"
      draggable
      latitude={coordinates[1]}
      longitude={coordinates[0]}
      onDrag={onMarkerDrag}
      onDragEnd={onMarkerDragEnd}
      onDragStart={onMarkerDragStart}
      style={{ cursor: 'move', height: dragging ? 36 : 24 }}
    >
      <Pin size={dragging ? 36 : 24} />
    </Marker>
  );
};
